import React from "react";
import { motion } from "framer-motion";
import { useMicrospecThemeLanguageContext } from "../../../MicrospecDynamicUI/Themes";
import {
  Box,
  Typography,
  Container,
  Grid,
  Button,
  IconButton,
} from "@mui/material";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SchoolIcon from "@mui/icons-material/School";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ScienceIcon from "@mui/icons-material/Science";
import FavoriteIcon from "@mui/icons-material/Favorite";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupsIcon from "@mui/icons-material/Groups";
import VerifiedIcon from "@mui/icons-material/Verified";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import LanguagesMenu from "../../../MicrospecDynamicUI/Components/LanguagesMenu";
import { useNavigate } from "react-router-dom";

/* ── Animated floating ambient orbs ───────────────────────────────── */
const AmbientOrb = ({ color, size, top, left, delay = 0, duration = 20 }) => (
  <motion.div
    className="absolute rounded-full pointer-events-none"
    style={{
      width: size,
      height: size,
      top,
      left,
      background: color,
      filter: `blur(${parseInt(size) * 0.6}px)`,
    }}
    animate={{
      x: [0, 40, -30, 20, 0],
      y: [0, -30, 20, -40, 0],
      scale: [1, 1.15, 0.9, 1.1, 1],
      opacity: [0.4, 0.6, 0.35, 0.55, 0.4],
    }}
    transition={{
      duration,
      repeat: Infinity,
      ease: "easeInOut",
      delay,
    }}
  />
);

/* ── Decorative DNA Helix Pattern ─────────────────────────────────── */
const DNAPattern = () => (
  <svg
    className="absolute right-0 top-1/4 w-32 h-96 opacity-[0.06] pointer-events-none"
    viewBox="0 0 100 300"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {[...Array(12)].map((_, i) => (
      <g key={i}>
        <motion.circle
          cx={50 + Math.sin(i * 0.5) * 30}
          cy={i * 25 + 10}
          r="4"
          fill="#8B5CF6"
          animate={{ opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 2, delay: i * 0.15, repeat: Infinity }}
        />
        <motion.circle
          cx={50 - Math.sin(i * 0.5) * 30}
          cy={i * 25 + 10}
          r="4"
          fill="#7393B3"
          animate={{ opacity: [0.3, 0.8, 0.3] }}
          transition={{ duration: 2, delay: i * 0.15 + 0.5, repeat: Infinity }}
        />
        <motion.line
          x1={50 + Math.sin(i * 0.5) * 30}
          y1={i * 25 + 10}
          x2={50 - Math.sin(i * 0.5) * 30}
          y2={i * 25 + 10}
          stroke="#C084FC"
          strokeWidth="1"
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 2, delay: i * 0.1, repeat: Infinity }}
        />
      </g>
    ))}
  </svg>
);

/* ── Credential Card Component ────────────────────────────────────── */
const CredentialCard = ({ icon: Icon, title, description, color, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay: index * 0.1 }}
    className="group"
  >
    <div
      className="relative h-full p-6 rounded-2xl backdrop-blur-xl border border-white/40 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.4) 100%)",
      }}
    >
      {/* Hover glow effect */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(circle at center, ${color}15 0%, transparent 70%)`,
        }}
      />

      <div className="relative z-10 flex flex-col h-full">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300"
          style={{
            background: `linear-gradient(135deg, ${color}, ${color}CC)`,
          }}
        >
          <Icon sx={{ color: "white", fontSize: 28 }} />
        </div>
        <Typography
          variant="h6"
          className="font-bold text-[#2D1B4E] mb-2"
          sx={{ fontSize: "1.1rem" }}
        >
          {title}
        </Typography>
        <Typography className="text-[#6B5B7B] text-sm leading-relaxed flex-grow">
          {description}
        </Typography>
      </div>
    </div>
  </motion.div>
);

/* ── Value Card Component ─────────────────────────────────────────── */
const ValueCard = ({ icon: Icon, title, color, index }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    className="flex flex-col items-center text-center p-6 group"
  >
    <motion.div
      className="w-20 h-20 rounded-2xl flex items-center justify-center mb-4 relative"
      style={{
        background: `linear-gradient(135deg, ${color}20, ${color}10)`,
        border: `2px solid ${color}30`,
      }}
      whileHover={{ scale: 1.1, rotate: 5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Icon sx={{ color, fontSize: 36 }} />
      <motion.div
        className="absolute inset-0 rounded-2xl"
        style={{ border: `2px solid ${color}` }}
        initial={{ scale: 1.2, opacity: 0 }}
        animate={{ scale: [1.2, 1.5, 1.2], opacity: [0, 0.3, 0] }}
        transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
      />
    </motion.div>
    <Typography className="font-semibold text-[#2D1B4E]">{title}</Typography>
  </motion.div>
);

/* ── Main About Page Component ────────────────────────────────────── */
const AboutPage = () => {
  const { localizationProvider, dir } = useMicrospecThemeLanguageContext();
  const navigate = useNavigate();
  const isRTL = dir === "rtl";

  // Translation helpers
  const t = (key) =>
    localizationProvider.translateByType({
      type: "Localized",
      text: key,
    });

  // Get all translations
  const nav = {
    brand: t("landing.nav.brand"),
    backToHome: t("aboutPage.nav.backToHome"),
  };

  const hero = {
    badge: t("aboutPage.hero.badge"),
    name: t("aboutPage.hero.name"),
    title: t("aboutPage.hero.title"),
    subtitle: t("aboutPage.hero.subtitle"),
  };

  const credentials = {
    sectionLabel: t("aboutPage.credentials.sectionLabel"),
    title: t("aboutPage.credentials.title"),
    subtitle: t("aboutPage.credentials.subtitle"),
    items: [0, 1, 2, 3].map((i) => ({
      title: t(`aboutPage.credentials.items.${i}.title`),
      description: t(`aboutPage.credentials.items.${i}.description`),
    })),
  };

  const philosophy = {
    sectionLabel: t("aboutPage.philosophy.sectionLabel"),
    title: t("aboutPage.philosophy.title"),
    quote: t("aboutPage.philosophy.quote"),
    description: t("aboutPage.philosophy.description"),
  };

  const values = {
    sectionLabel: t("aboutPage.values.sectionLabel"),
    title: t("aboutPage.values.title"),
    items: [0, 1, 2, 3, 4].map((i) => t(`aboutPage.values.items.${i}`)),
  };

  const contact = {
    title: t("aboutPage.contact.title"),
    subtitle: t("aboutPage.contact.subtitle"),
    cta: t("aboutPage.contact.cta"),
    email: t("aboutPage.contact.email"),
    phone: t("aboutPage.contact.phone"),
    location: t("aboutPage.contact.location"),
  };

  const whatsappMessage = t("landing.whatsappMessage");
  const WHATSAPP_PHONE = "97252644398";
  const getWhatsAppUrl = (phone, message) =>
    `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

  const credentialIcons = [
    SchoolIcon,
    WorkspacePremiumIcon,
    ScienceIcon,
    EmojiEventsIcon,
  ];
  const credentialColors = ["#7393B3", "#8B5CF6", "#C084FC", "#F59E0B"];

  const valueIcons = [
    FavoriteIcon,
    AutoAwesomeIcon,
    GroupsIcon,
    VerifiedIcon,
    PsychologyIcon,
  ];
  const valueColors = ["#EC4899", "#8B5CF6", "#7393B3", "#10B981", "#F59E0B"];

  return (
    <Box
      className="w-full flex flex-col min-h-screen scroll-smooth relative"
      sx={{
        scrollBehavior: "smooth",
        background:
          "linear-gradient(180deg, #FDFBFE 0%, #F3EAFA 8%, #EDE4F8 18%, #F5EBF6 28%, #F8EAF0 40%, #FDF5F0 52%, #F9F0F7 64%, #EDE4F8 76%, #F5EBF6 88%, #FDFBFE 100%)",
      }}
    >
      {/* Global ambient floating orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <AmbientOrb
          color="rgba(139,92,246,0.12)"
          size="450px"
          top="-5%"
          left="60%"
          delay={0}
          duration={25}
        />
        <AmbientOrb
          color="rgba(192,132,252,0.10)"
          size="400px"
          top="25%"
          left="-10%"
          delay={3}
          duration={22}
        />
        <AmbientOrb
          color="rgba(245,225,236,0.18)"
          size="380px"
          top="50%"
          left="70%"
          delay={6}
          duration={28}
        />
        <AmbientOrb
          color="rgba(115,147,179,0.14)"
          size="420px"
          top="75%"
          left="20%"
          delay={2}
          duration={20}
        />
      </div>

      {/* Navigation Bar */}
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="sticky top-0 z-50 bg-white/50 backdrop-blur-3xl shadow-xl shadow-[#8B5CF6]/5"
        style={{
          borderBottom: "1px solid rgba(139,92,246,0.08)",
        }}
      >
        <Container maxWidth="xl" dir={dir}>
          <div className="flex items-center justify-between py-4 px-4 md:px-8">
            <a
              href="/"
              className="flex items-center gap-3 no-underline group"
              onClick={(e) => {
                e.preventDefault();
                navigate("/");
              }}
            >
              <div className="relative">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#7393B3] via-[#8B5CF6] to-[#C084FC] flex items-center justify-center shadow-lg shadow-[#8B5CF6]/25 group-hover:shadow-[#8B5CF6]/40 transition-shadow duration-300">
                  <PsychologyIcon sx={{ color: "white", fontSize: 24 }} />
                </div>
              </div>
              <Typography
                variant="h6"
                className="font-bold tracking-tight text-slate-800"
              >
                {nav.brand}
              </Typography>
            </a>

            <div className="flex items-center gap-2">
              <Button
                variant="text"
                onClick={() => navigate("/")}
                startIcon={isRTL ? <ArrowForwardIcon /> : <ArrowBackIcon />}
                sx={{
                  color: "#6B5B7B",
                  fontWeight: 500,
                  textTransform: "none",
                  "&:hover": {
                    background: "rgba(139,92,246,0.08)",
                    color: "#8B5CF6",
                  },
                }}
              >
                {nav.backToHome}
              </Button>
              <LanguagesMenu />
            </div>
          </div>
        </Container>
      </motion.nav>

      {/* Hero Section */}
      <Box className="relative py-20 md:py-32 overflow-hidden">
        <DNAPattern />

        <Container maxWidth="lg" dir={dir}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center relative z-10"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-8"
              style={{
                background:
                  "linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(192,132,252,0.1) 100%)",
                border: "1px solid rgba(139,92,246,0.2)",
              }}
            >
              <PsychologyIcon sx={{ color: "#8B5CF6", fontSize: 20 }} />
              <Typography
                className="font-semibold text-sm"
                sx={{ color: "#7C3AED" }}
              >
                {hero.badge}
              </Typography>
            </motion.div>

            {/* Avatar */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="relative mx-auto mb-8 w-40 h-40 md:w-48 md:h-48"
            >
              <div
                className="w-full h-full rounded-full flex items-center justify-center shadow-2xl shadow-[#8B5CF6]/20"
                style={{
                  background:
                    "linear-gradient(135deg, #7393B3 0%, #8B5CF6 50%, #C084FC 100%)",
                }}
              >
                <Typography
                  className="text-white font-bold"
                  sx={{ fontSize: "3.5rem" }}
                >
                  {hero.name.charAt(0)}
                </Typography>
              </div>
              {/* Decorative rings */}
              <motion.div
                className="absolute inset-[-8px] rounded-full border-2 border-[#8B5CF6]/20"
                animate={{ scale: [1, 1.1, 1], opacity: [0.5, 0.2, 0.5] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.div
                className="absolute inset-[-16px] rounded-full border border-[#C084FC]/15"
                animate={{ scale: [1, 1.15, 1], opacity: [0.3, 0.1, 0.3] }}
                transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
              />
            </motion.div>

            {/* Name & Title */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              <Typography
                variant="h2"
                className="font-bold tracking-tight mb-3"
                sx={{
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  background:
                    "linear-gradient(135deg, #2D1B4E 0%, #7C3AED 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                {hero.name}
              </Typography>
              <Typography
                variant="h5"
                className="font-medium mb-6"
                sx={{
                  color: "#7393B3",
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                }}
              >
                {hero.title}
              </Typography>
              <Typography
                className="max-w-2xl mx-auto leading-relaxed"
                sx={{
                  color: "#6B5B7B",
                  fontSize: { xs: "1rem", md: "1.1rem" },
                }}
              >
                {hero.subtitle}
              </Typography>
            </motion.div>
          </motion.div>
        </Container>
      </Box>

      {/* Credentials Section */}
      <Box className="py-20 relative">
        <div
          className="absolute inset-0 opacity-[0.15] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(139,92,246,0.3) 1px, transparent 1px)",
            backgroundSize: "40px 40px",
          }}
        />

        <Container maxWidth="xl" dir={dir} className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <Typography
              variant="overline"
              className="font-bold tracking-[0.2em] mb-3 block"
              sx={{
                background: "linear-gradient(135deg, #7393B3, #8B5CF6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {credentials.sectionLabel}
            </Typography>
            <Typography
              variant="h3"
              className="font-bold tracking-tight text-[#2D1B4E] mb-4"
              sx={{ fontSize: { xs: "1.8rem", md: "2.5rem" } }}
            >
              {credentials.title}
            </Typography>
            <Typography className="text-lg text-[#6B5B7B] max-w-2xl mx-auto">
              {credentials.subtitle}
            </Typography>
          </motion.div>

          <Grid container spacing={3} className="px-4 md:px-8">
            {credentials.items.map((item, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <CredentialCard
                  icon={credentialIcons[index]}
                  title={item.title}
                  description={item.description}
                  color={credentialColors[index]}
                  index={index}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Philosophy Section */}
      <Box className="py-24 relative overflow-hidden">
        <motion.div
          className="absolute top-0 right-[-10%] w-[40%] h-[60%] rounded-full bg-[#E0CDF5]/30 blur-[150px] pointer-events-none"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 12, repeat: Infinity }}
        />

        <Container maxWidth="lg" dir={dir} className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center"
          >
            <Typography
              variant="overline"
              className="font-bold tracking-[0.2em] mb-3 block"
              sx={{
                background: "linear-gradient(135deg, #7393B3, #8B5CF6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {philosophy.sectionLabel}
            </Typography>

            <div
              className="relative max-w-3xl mx-auto p-8 md:p-12 rounded-3xl mt-8"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.5) 100%)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(139,92,246,0.15)",
                boxShadow: "0 20px 60px rgba(139,92,246,0.1)",
              }}
            >
              {/* Quote marks */}
              <Typography
                sx={{
                  fontSize: "5rem",
                  lineHeight: 1,
                  color: "rgba(139,92,246,0.15)",
                  position: "absolute",
                  top: "1rem",
                  left: "1.5rem",
                  fontFamily: "Georgia, serif",
                }}
              >
                "
              </Typography>

              <Typography
                variant="h4"
                className="font-bold text-[#2D1B4E] mb-6 relative z-10"
                sx={{ fontSize: { xs: "1.5rem", md: "2rem" } }}
              >
                {philosophy.title}
              </Typography>

              <Typography
                className="text-xl md:text-2xl italic text-[#6B5B7B] mb-6 leading-relaxed"
                sx={{ fontFamily: "Georgia, serif" }}
              >
                {philosophy.quote}
              </Typography>

              <Typography className="text-[#6B5B7B] leading-relaxed">
                {philosophy.description}
              </Typography>
            </div>
          </motion.div>
        </Container>
      </Box>

      {/* Values Section */}
      <Box className="py-20 relative">
        <Container maxWidth="lg" dir={dir}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <Typography
              variant="overline"
              className="font-bold tracking-[0.2em] mb-3 block"
              sx={{
                background: "linear-gradient(135deg, #7393B3, #8B5CF6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {values.sectionLabel}
            </Typography>
            <Typography
              variant="h3"
              className="font-bold tracking-tight text-[#2D1B4E]"
              sx={{ fontSize: { xs: "1.8rem", md: "2.5rem" } }}
            >
              {values.title}
            </Typography>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {values.items.map((item, index) => (
              <ValueCard
                key={index}
                icon={valueIcons[index]}
                title={item}
                color={valueColors[index]}
                index={index}
              />
            ))}
          </div>
        </Container>
      </Box>

      {/* Contact CTA Section */}
      <Box className="py-24 relative overflow-hidden">
        <motion.div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, #0A0F2E 0%, #131836 50%, #1A1F3C 100%)",
          }}
        />

        {/* Ambient decorations */}
        <motion.div
          className="absolute top-[10%] left-[5%] w-[30%] h-[40%] rounded-full bg-[#8B5CF6]/10 blur-[100px] pointer-events-none"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-[10%] right-[5%] w-[25%] h-[35%] rounded-full bg-[#7393B3]/10 blur-[80px] pointer-events-none"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 12, repeat: Infinity, delay: 2 }}
        />

        <Container maxWidth="md" dir={dir} className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center"
          >
            <Typography
              variant="h3"
              className="font-bold text-white mb-4"
              sx={{ fontSize: { xs: "1.8rem", md: "2.5rem" } }}
            >
              {contact.title}
            </Typography>
            <Typography
              className="text-slate-400 mb-10 max-w-xl mx-auto"
              sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }}
            >
              {contact.subtitle}
            </Typography>

            {/* Contact Info Cards */}
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-10">
              {[
                {
                  icon: EmailIcon,
                  text: contact.email,
                  action: () =>
                    (window.location.href = `mailto:${contact.email}`),
                },
                {
                  icon: PhoneIcon,
                  text: contact.phone,
                  action: () =>
                    window.open(
                      getWhatsAppUrl(WHATSAPP_PHONE, whatsappMessage),
                      "_blank",
                    ),
                },
                { icon: LocationOnIcon, text: contact.location },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  className={`flex items-center gap-3 px-5 py-3 rounded-xl ${item.action ? "cursor-pointer" : ""}`}
                  onClick={item.action}
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <item.icon sx={{ color: "#8B5CF6", fontSize: 22 }} />
                  <Typography className="text-slate-300 text-sm">
                    {item.text}
                  </Typography>
                </motion.div>
              ))}
            </div>

            <Button
              variant="contained"
              size="large"
              onClick={() =>
                window.open(
                  getWhatsAppUrl(WHATSAPP_PHONE, whatsappMessage),
                  "_blank",
                )
              }
              sx={{
                background: "linear-gradient(135deg, #7393B3, #8B5CF6)",
                borderRadius: "9999px",
                px: 6,
                py: 1.8,
                fontSize: "1rem",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 10px 30px rgba(139,92,246,0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #5A7A9A, #7C3AED)",
                  boxShadow: "0 15px 40px rgba(139,92,246,0.4)",
                },
              }}
            >
              {contact.cta}
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* Footer */}
      <Box className="py-8 relative" sx={{ background: "#050816" }}>
        <Container maxWidth="xl" dir={dir}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 px-4">
            <Typography className="text-slate-500 text-sm">
              © {new Date().getFullYear()} {nav.brand}.{" "}
              {t("landing.footer.rights")}
            </Typography>
            <div className="flex items-center gap-2">
              <a
                href="/"
                className="flex items-center gap-2 no-underline"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/");
                }}
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#7393B3] via-[#8B5CF6] to-[#C084FC] flex items-center justify-center">
                  <PsychologyIcon sx={{ color: "white", fontSize: 16 }} />
                </div>
                <Typography className="text-slate-400 text-sm font-medium">
                  {nav.brand}
                </Typography>
              </a>
            </div>
          </div>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
